import React from 'react'
import cx from 'classnames'

import Icon, { EIconId } from '../icons'
import ClientApi from '../../api/clientApi'
import { ELocalStorageKeys } from '../../utils/consts'
import { getLocalStorageItem, setLocalStorageItem } from '../../utils'
import { getContent } from './utils'

import ELocale from '../../types/locales'

export const enum ECountryCode {
  DE = 'DE',
  AT = 'AT',
  CH = 'CH',
  UNKNOWN = 'UNKNOWN',
}

const CountrySelector: React.FC<{
  countryCodeUrl: string
  locale: ELocale
}> = (props) => {
  const [countryCode, setCountryCode] = React.useState<ECountryCode>(ECountryCode.UNKNOWN)
  const [countrySelected, setCountrySelected] = React.useState<boolean>(false)
  const [closeButtonState, setCloseButtonState] = React.useState<boolean>(false)
  const [referral, setReferral] = React.useState<string>('none')

  const COUNTRY_SELECT_COOKIE_PREFIX = 'cs=true'
  const content = getContent(props.locale)

  React.useEffect(() => {
    const countryCode = getLocalStorageItem(ELocalStorageKeys.COUNTRY_CODE) as ECountryCode
    if (countryCode) {
      setCountryCode(countryCode)
      return // early exit when country code is set
    }
    try {
      ;(async () => {
        const response = await ClientApi.getCountryCode()
        const countryCode = response.data.country || ECountryCode.UNKNOWN
        setLocalStorageItem(ELocalStorageKeys.COUNTRY_CODE, countryCode)
        setCountryCode(countryCode)
      })()
    } catch (e) {
      console.error(e)
    }
  }, [])

  React.useEffect(() => {
    const hasCountrySelectedCookie = document.cookie.indexOf(COUNTRY_SELECT_COOKIE_PREFIX) >= 0

    if (hasCountrySelectedCookie) {
      setCountrySelected(true)
    }
    if (countrySelected) {
      document.cookie = `${COUNTRY_SELECT_COOKIE_PREFIX}; path=/; SameSite=Strict;`
    }
  }, [countrySelected, closeButtonState])

  const showPopup = (condition): boolean => {
    switch (condition) {
      case ECountryCode.AT:
        return props.locale != ELocale.DE_AT && !countrySelected && !closeButtonState
      case ECountryCode.CH:
        return props.locale != ELocale.DE_CH && !countrySelected && !closeButtonState
      case ECountryCode.DE:
        return props.locale != ELocale.DE_DE && !countrySelected && !closeButtonState
      default:
        return false
    }
  }

  const showModal = showPopup(countryCode)

  const handleOnClickButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setCountrySelected(true)
    if (referral === `www.hse.de` && props.locale === ELocale.DE_DE) return
    else if (referral === `www.hse.at` && props.locale === ELocale.DE_AT) return
    else if (referral === 'www.hse24.ch' && props.locale === ELocale.DE_CH) return
    location.assign(`https://${referral}`)
  }

  const handleOnOptionClick = (link: string) => {
    setReferral(link)
  }

  const handleOnClickClose = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    setCloseButtonState(true)
  }

  return showModal ? (
    <div
      className="flex fixed w-full h-full inset-x-0 inset-y-0 z-30 justify-center items-center"
      style={{ backgroundColor: 'rgba(0,0,0,.3)' }}
      data-testid="country-selector-overlay"
    >
      <div className="flex flex-col relative px-16 py-24 mx-16 bg-white uif-shadow sm:py-32 sm:px-48 sm:uif-max-w-560 md:px-48">
        <Icon
          id={EIconId.CLOSE_IN_CIRCLE}
          className="w-32 absolute right-0 top-0 mr-8 mt-8 sm:mr-16 sm:mt-16"
          onClick={handleOnClickClose}
        />
        <img className="mx-auto" src={content.imageSrc} alt={content.label} />
        <p className="text-18 leading-24 text-20 leading-28 mt-24">Willkommen bei HSE</p>
        <p className="mt-4 text-iron">{content.description}</p>
        <ul className="flex flex-col mt-8 -mb-8">
          {content.options.map((option, index) => (
            <li
              className="flex items-center py-8 cursor-pointer text-white hover:text-lightgrey-lightest"
              key={option.link}
              onClick={() => handleOnOptionClick(option.link)}
            >
              <div className="flex justify-center items-center w-20 h-20 mr-12 rounded-full bg-current border border-dark transition-color duration-150">
                {referral === option.link && <span className="block rounded-full w-8 h-8 bg-brand-orange" />}
              </div>
              <span className="text-iron">
                {`${option.name}: Auf `}
                <a href={`https://${option.link}`} className="underline">
                  {option.link}
                </a>
                {index === 0 ? ' bleiben' : ' wechseln'}
              </span>
            </li>
          ))}
        </ul>
        <hr className="mt-24 mb-24 border-t border-lightgrey-light" />
        <button
          className={cx('h-48 w-full px-48 text-white font-bold outline-none', {
            'bg-grey cursor-not-allowed': referral === 'none',
            'bg-brand-orange cursor-pointer': referral !== 'none',
          })}
          onClick={handleOnClickButton}
          data-id="primary"
        >
          Weiter
        </button>
      </div>
    </div>
  ) : null
}

export default CountrySelector
